/* eslint-disable */
/**
 * BlogContext — shared blog state backed by REST API + localStorage fallback.
 * 
 * Priority:
 *   1. Fetch from Blog API server (if REACT_APP_BLOG_API_URL is set & reachable)
 *   2. Fall back to localStorage (for offline/dev use or when API is down)
 *
 * All CRUD operations live here so both the admin dashboard
 * and the public blog pages read from the same data store.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'lawfirm_blog_posts';
const API_BASE = process.env.REACT_APP_BLOG_API_URL || '';
const API_KEY = process.env.REACT_APP_BLOG_API_KEY || '';

const SEED_POSTS = [
  {
    id: '1',
    title: 'Panduan Lengkap Proses Gugatan Cerai di Indonesia',
    slug: 'panduan-proses-gugatan-cerai',
    category: 'Hukum Keluarga',
    excerpt: 'Memahami proses hukum perceraian adalah langkah pertama yang penting. Artikel ini menjelaskan tahapan, dokumen yang diperlukan, dan hak-hak setiap pihak.',
    content: `Proses gugatan cerai di Indonesia diatur oleh Undang-Undang No. 1 Tahun 1974 tentang Perkawinan. Terdapat dua jenis perceraian: cerai talak (diajukan suami) dan cerai gugat (diajukan istri).

## Tahapan Proses Perceraian

1. **Pengajuan gugatan** ke Pengadilan Agama (Muslim) atau Pengadilan Negeri (non-Muslim)
2. **Mediasi wajib** — pengadilan akan menunjuk mediator untuk mencoba mendamaikan kedua pihak
3. **Sidang pemeriksaan** — jika mediasi gagal, sidang dilanjutkan dengan pemeriksaan saksi
4. **Putusan pengadilan** — hakim akan mengeluarkan putusan berdasarkan bukti dan kesaksian

## Dokumen yang Diperlukan

- Surat Nikah asli
- KTP suami dan istri
- Kartu Keluarga
- Akta lahir anak (jika ada)
- Surat gugatan

Konsultasikan dengan pengacara kami untuk mendapatkan panduan yang lebih spesifik sesuai situasi Anda.`,
    author: 'Tim Hukum Kantor Kami',
    publishedAt: new Date('2025-05-01').toISOString(),
    status: 'published',
    coverColor: '#1a2d5a',
    readTime: '5 menit',
  },
  {
    id: '2',
    title: 'Syarat dan Prosedur Mendirikan PT di Indonesia',
    slug: 'syarat-prosedur-mendirikan-pt',
    category: 'Hukum Bisnis',
    excerpt: 'Mendirikan Perseroan Terbatas (PT) memerlukan beberapa tahapan legal. Pelajari persyaratan modal, dokumen, dan prosedur pendaftarannya di sini.',
    content: `Perseroan Terbatas (PT) adalah badan hukum yang paling umum digunakan untuk kegiatan bisnis di Indonesia. Pendirian PT diatur dalam UU No. 40 Tahun 2007.

## Syarat Utama Pendirian PT

- **Minimal 2 pendiri** (perorangan atau badan hukum)
- **Modal dasar** minimal Rp 50.000.000 (dapat lebih rendah untuk UMKM)
- **Akta pendirian** yang dibuat di hadapan Notaris
- **Pengesahan Kemenkumham** melalui sistem AHU Online

## Tahapan Pendirian PT

1. Konsultasi dan persiapan dokumen
2. Pembuatan Akta Pendirian oleh Notaris
3. Pengesahan oleh Kementerian Hukum dan HAM
4. Pendaftaran NPWP dan NIB (Nomor Induk Berusaha)
5. Pengurusan izin usaha sesuai bidang

Hubungi tim kami untuk mendapatkan bantuan lengkap dalam proses pendirian PT Anda.`,
    author: 'Tim Hukum Kantor Kami',
    publishedAt: new Date('2025-05-10').toISOString(),
    status: 'published',
    coverColor: '#0f3460',
    readTime: '4 menit',
  },
  {
    id: '3',
    title: 'Hak-Hak Karyawan yang Di-PHK: Yang Perlu Anda Ketahui',
    slug: 'hak-karyawan-phk',
    category: 'Hukum Ketenagakerjaan',
    excerpt: 'Pemutusan Hubungan Kerja (PHK) adalah situasi yang sulit. Namun hukum Indonesia memberikan perlindungan bagi karyawan. Ketahui hak-hak Anda.',
    content: `Pemutusan Hubungan Kerja (PHK) diatur dalam Undang-Undang No. 13 Tahun 2003 tentang Ketenagakerjaan yang telah diubah oleh UU Cipta Kerja.

## Hak-Hak Karyawan yang Di-PHK

### 1. Uang Pesangon
Dihitung berdasarkan masa kerja:
- < 1 tahun: 1 bulan upah
- 1–2 tahun: 2 bulan upah
- Dan seterusnya sesuai ketentuan

### 2. Uang Penghargaan Masa Kerja
Diberikan untuk karyawan dengan masa kerja 3 tahun ke atas.

### 3. Uang Penggantian Hak
Mencakup cuti tahunan yang belum diambil, biaya perumahan, dan pengobatan.

## Langkah yang Harus Dilakukan

1. Minta surat PHK tertulis dari perusahaan
2. Pastikan semua hak terpenuhi sebelum menandatangani surat apapun
3. Konsultasikan dengan pengacara jika ada ketidaksesuaian

Jangan ragu untuk menghubungi kami jika Anda merasa hak Anda tidak terpenuhi.`,
    author: 'Tim Hukum Kantor Kami',
    publishedAt: new Date('2025-05-18').toISOString(),
    status: 'published',
    coverColor: '#16213e',
    readTime: '6 menit',
  },
];

const BlogContext = createContext(null);

// ─── localStorage helpers (fallback) ────────────────────────────────────────
function loadPostsLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_POSTS));
  return SEED_POSTS;
}

function savePostsLocal(posts) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
}

// ─── API helpers ────────────────────────────────────────────────────────────
function apiHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  if (API_KEY) headers['x-api-key'] = API_KEY;
  return headers;
}

async function apiFetch(endpoint, options = {}) {
  if (!API_BASE) return null;
  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...apiHeaders(), ...options.headers },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  } catch (err) {
    console.warn('[BlogContext] API request failed, using localStorage fallback:', err.message);
    return null;
  }
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// ─── Provider ───────────────────────────────────────────────────────────────
export function BlogProvider({ children }) {
  const [posts, setPosts] = useState(loadPostsLocal);
  const [apiAvailable, setApiAvailable] = useState(false);
  const initialFetchDone = useRef(false);

  // On mount, try to fetch from API. If successful, sync to localStorage.
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    (async () => {
      const result = await apiFetch('/posts');
      if (result && result.data) {
        setApiAvailable(true);
        setPosts(result.data);
        savePostsLocal(result.data);
        console.log(`[BlogContext] ✅ Synced ${result.data.length} posts from API`);
      } else {
        console.log('[BlogContext] Using localStorage (API not available)');
      }
    })();
  }, []);

  // Periodic sync — re-fetch from API every 30 seconds to pick up n8n posts
  useEffect(() => {
    if (!API_BASE) return;
    const interval = setInterval(async () => {
      const result = await apiFetch('/posts');
      if (result && result.data) {
        setApiAvailable(true);
        setPosts(result.data);
        savePostsLocal(result.data);
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const persist = useCallback(async (updated) => {
    setPosts(updated);
    savePostsLocal(updated);
  }, []);

  const createPost = useCallback(async (data) => {
    const newPost = {
      ...data,
      id: generateId(),
      slug: slugify(data.title),
      publishedAt: new Date().toISOString(),
      status: data.status || 'published',
      author: data.author || 'Tim Hukum Kantor Kami',
      readTime: `${Math.ceil((data.content || '').split(' ').length / 200)} menit`,
      coverColor: data.coverColor || '#1a2d5a',
    };

    // Try API first
    if (apiAvailable) {
      const result = await apiFetch('/posts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (result && result.data) {
        const updatedPosts = [result.data, ...posts];
        persist(updatedPosts);
        return result.data;
      }
    }

    // Fallback to local
    persist([newPost, ...posts]);
    return newPost;
  }, [posts, persist, apiAvailable]);

  const updatePost = useCallback(async (id, data) => {
    // Try API first
    if (apiAvailable) {
      const result = await apiFetch(`/posts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (result && result.data) {
        const updated = posts.map((p) => (p.id === id ? result.data : p));
        persist(updated);
        return;
      }
    }

    // Fallback to local
    const updated = posts.map((p) =>
      p.id === id
        ? {
            ...p,
            ...data,
            slug: slugify(data.title || p.title),
            readTime: `${Math.ceil((data.content || p.content || '').split(' ').length / 200)} menit`,
          }
        : p
    );
    persist(updated);
  }, [posts, persist, apiAvailable]);

  const deletePost = useCallback(async (id) => {
    // Try API first
    if (apiAvailable) {
      const result = await apiFetch(`/posts/${id}`, { method: 'DELETE' });
      if (result && result.success) {
        persist(posts.filter((p) => p.id !== id));
        return;
      }
    }

    // Fallback to local
    persist(posts.filter((p) => p.id !== id));
  }, [posts, persist, apiAvailable]);

  const getPost = useCallback((idOrSlug) => {
    return posts.find((p) => p.id === idOrSlug || p.slug === idOrSlug) || null;
  }, [posts]);

  // Manual refresh — can be called from dashboard to pull latest n8n posts
  const refreshPosts = useCallback(async () => {
    const result = await apiFetch('/posts');
    if (result && result.data) {
      setApiAvailable(true);
      setPosts(result.data);
      savePostsLocal(result.data);
      return true;
    }
    return false;
  }, []);

  const publishedPosts = posts.filter((p) => p.status === 'published');

  return (
    <BlogContext.Provider value={{
      posts,
      publishedPosts,
      createPost,
      updatePost,
      deletePost,
      getPost,
      refreshPosts,
      apiAvailable,
    }}>
      {children}
    </BlogContext.Provider>
  );
}

export function useBlog() {
  const ctx = useContext(BlogContext);
  if (!ctx) throw new Error('useBlog must be used inside <BlogProvider>');
  return ctx;
}
